// src/utils/dataLoaders.js
// Функции для загрузки данных с API

const API_URL = "https://apitr.subxonqulov.uz/api/vazifauser/analytics/";

export async function loadAnalyticsData() {
  const res = await fetch(API_URL);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
}

export async function loadCourses() {
  const res = await fetch("https://apitr.subxonqulov.uz/api/fanlar/");
  return res.ok ? await res.json() : [];
}

export async function loadSubjects() {
  const res = await fetch("https://apitr.subxonqulov.uz/api/fanlar/");
  return res.ok ? await res.json() : [];
}

export async function loadAnswersByCourse(courseId, userId = null) {
  let url = `https://apitr.subxonqulov.uz/api/yozma-javoblar/?kurs=${courseId}`;
  if (userId) {
    url += `&user=${userId}`;
  }
  const res = await fetch(url);
  return res.ok ? await res.json() : [];
}

export async function loadAllAnswers() {
  const res = await fetch("https://apitr.subxonqulov.uz/api/yozma-javoblar/");
  return res.ok ? await res.json() : [];
}

export async function loadTestsByCourse(courseId) {
  const res = await fetch(
    `https://apitr.subxonqulov.uz/api/kurs-yozma-savollar/?kurs=${courseId}`
  );
  return res.ok ? await res.json() : [];
}

export async function loadUserInfo(userId) {
  const res = await fetch(`https://apitr.subxonqulov.uz/api/users/${userId}/`);
  if (res.ok) {
    const userData = await res.json();
    return {
      username: userData.username || userData.first_name || `Talaba ${userId}`,
      fullName: `${userData.first_name || ""} ${userData.last_name || ""}`.trim(),
    };
  }
  return null;
}

export function getUserIdFromStorage() {
  try {
    const authUser = localStorage.getItem("auth:user");
    if (authUser) {
      const userData = JSON.parse(authUser);
      return userData.id;
    }
  } catch (e) {
    console.warn("Ошибка при чтении auth:user из localStorage:", e);
  }
  return null;
}

// Подготовка данных для диаграммы оценок по курсам
export async function prepareMarksData(courses, userId) {
  const marksData = [];
  const courseDataMap = new Map();

  if (!userId) {
    console.warn("User ID не найден в localStorage");
    return { marksData, courseDataMap };
  }

  for (const course of courses) {
    try {
      const allTests = await loadTestsByCourse(course.id);
      const answers = await loadAnswersByCourse(course.id, userId);

      const marks = answers
        .filter((a) => a.ball && a.ball >= 55)
        .map((a) => a.ball);

      const avgMark = marks.length > 0
        ? marks.reduce((sum, m) => sum + m, 0) / marks.length
        : 0;

      const courseInfo = {
        courseName: course.name.substring(0, 20),
        marks: marks,
        avgMark: avgMark,
        answers: answers,
        allTests: allTests,
      };

      marksData.push(courseInfo);

      if (marks.length > 0) {
        courseDataMap.set(course.id, {
          ...courseInfo,
          fullName: course.name,
        });
      }
    } catch (e) {
      console.warn(`Ошибка загрузки данных для курса ${course.name}:`, e);
    }
  }

  return { marksData, courseDataMap };
}

// Подготовка данных для одного курса
export async function prepareSingleCourseData(courseId, courseDataMap) {
  if (!courseDataMap.has(courseId)) {
    return null;
  }

  const courseData = courseDataMap.get(courseId);

  const allTestsArray = (courseData.allTests || []).map((test, idx) => ({
    id: test.id,
    name: `Nazorat ${idx + 1}`,
    tartib: test.tartib || idx + 1,
  }));

  // Группируем ответы по savol (контрольной работе)
  const answersByTest = new Map();
  
  courseData.answers.forEach((answer) => {
    if (!answersByTest.has(answer.savol)) {
      answersByTest.set(answer.savol, []);
    }
    answersByTest.get(answer.savol).push(answer);
  });

  const passedMarks = new Map();
  
  // Для каждой контрольной работы берем только лучший результат >= 55
  answersByTest.forEach((testAnswers, savolId) => {
    // Сортируем по баллам в убывающем порядке
    const sortedByScore = testAnswers
      .filter((a) => a.ball && a.ball >= 55) // Только пройденные
      .sort((a, b) => b.ball - a.ball); // От большего к меньшему

    if (sortedByScore.length > 0) {
      // Берем ответ с максимальным баллом
      const bestAnswer = sortedByScore[0];
      passedMarks.set(savolId, {
        ball: bestAnswer.ball,
        attempts: testAnswers.length, // Общее количество попыток
      });
    }
  });

  // Сортируем результаты по порядку контрольных в курсе
  const sortedResults = Array.from(passedMarks.entries())
    .map(([savolId, data]) => ({
      savolId,
      ...data,
    }))
    .sort((a, b) => a.savolId - b.savolId);

  return {
    courseId,
    courseName: courseData.fullName,
    allTestsArray,
    passedMarks: new Map(sortedResults.map(r => [r.savolId, r.ball])),
    passedMarksWithAttempts: sortedResults, // Для отладки: включает информацию о попытках
    totalTests: allTestsArray.length,
    totalPassed: sortedResults.length,
  };
}

// Подготовка данных студентов по курсу
export async function prepareStudentsData(courseId) {
  try {
    const allAnswers = await loadAnswersByCourse(courseId);

    const studentMarksMap = new Map();
    const userIds = new Set();

    // Фильтруем только ответы с баллом >= 55
    allAnswers.forEach((answer) => {
      if (answer.user && answer.ball && answer.ball >= 55) { // Изменено: >= 55
        userIds.add(answer.user);
        if (!studentMarksMap.has(answer.user)) {
          studentMarksMap.set(answer.user, {
            userId: answer.user,
            marks: [],
          });
        }
        studentMarksMap.get(answer.user).marks.push(answer.ball);
      }
    });

    const usersMap = new Map();
    for (const userId of userIds) {
      try {
        const userInfo = await loadUserInfo(userId);
        if (userInfo) {
          usersMap.set(userId, userInfo);
        }
      } catch (err) {
        console.warn(`Ошибка загрузки пользователя ${userId}:`, err);
      }
    }

    const studentsData = Array.from(studentMarksMap.values()).map(
      (student) => {
        const avgMark =
          student.marks.length > 0
            ? student.marks.reduce((sum, m) => sum + m, 0) / student.marks.length
            : 0;
        const userInfo = usersMap.get(student.userId) || {};
        return {
          userId: student.userId,
          username: userInfo.username || `Talaba ${student.userId}`,
          userName: userInfo.username || `Talaba ${student.userId}`,
          userFullName: userInfo.fullName || `Talaba ${student.userId}`,
          avgMark: avgMark,
        };
      }
    );

    if (studentsData.length === 0) {
      studentsData.push({
        userId: 0,
        username: "Ma'lumot yo'q",
        userName: "Ma'lumot yo'q",
        avgMark: 0,
      });
    }

    return studentsData;
  } catch (e) {
    console.warn(`Ошибка загрузки студентов для курса ${courseId}:`, e);
    return [];
  }
}

// Подготовка данных по предметам
export async function prepareSubjectsData(subjects) {
  try {
    const allAnswersForSubjects = await loadAllAnswers();

    const subjectsData = subjects
      .filter(subject => subject && subject.id) // Фильтруем null и пустые значения
      .map((subject) => {
        // Фильтруем только ответы с баллом >= 55
        const subjectAnswers = allAnswersForSubjects.filter(
          (answer) => answer.kurs === subject.id && answer.ball && answer.ball >= 55 // Изменено: >= 55
        );

        const avgMark =
          subjectAnswers.length > 0
            ? subjectAnswers.reduce((sum, answer) => sum + answer.ball, 0) /
              subjectAnswers.length
            : 0;

        return {
          subjectId: subject.id,
          subjectName: subject.name || `Fan ${subject.id}`,
          avgMark: avgMark,
          totalMarks: subjectAnswers.reduce(
            (sum, answer) => sum + answer.ball,
            0
          ),
          testCount: subjectAnswers.length,
        };
      })
      .filter((s) => s && s.testCount > 0); // Фильтруем после маппинга

    if (subjectsData.length === 0) {
      const fallbackSubjectsData = subjects
        .filter(subject => subject && subject.id)
        .map((subject) => ({
          subjectId: subject.id,
          subjectName: subject.name || `Fan ${subject.id}`,
          avgMark: 0,
          totalMarks: 0,
          testCount: 0,
        }));
      return fallbackSubjectsData;
    }

    return subjectsData;
  } catch (e) {
    console.warn("Ошибка загрузки данных по предметам:", e);
    return [];
  }
}
